import {
  brandColor,
  type BrandPalette,
  type BrandShade,
} from '../../styles/brand-colors';

/**
 * The flashcards empty-state hero — a fanned stack of cards around a sparkle
 * tile — its box and its palette ROLES, the one place both platforms read them
 * from. The animated hero is the Rive (`flashcards-empty.riv`, contract
 * `@flashka/shared/rive/flashcards-empty-rive`), whose colour roles are set
 * from this palette; `FlashcardsEmptyArtwork` draws the same picture still.
 *
 * This is the artwork's default box in DS points (web's long-standing 200×160).
 */
export const FLASHCARDS_EMPTY_ARTWORK_SIZE = { width: 200, height: 160 } as const;

/**
 * The palette ROLES of the composition, as DS tokens per scheme. Raw colours
 * because SVG fills are props, not classes (the exception
 * `NoQuizzesIllustration` documents) — but read from the palette, never typed
 * as hex. Dark is not the plain mirror: the neutrals sit one step lighter so
 * the stack still reads on a near-black surface, and the lift tint is deep
 * violet.
 */
export function flashcardsEmptyArtworkPalette(isDark: boolean) {
  const role = (palette: BrandPalette, light: BrandShade, dark: BrandShade) =>
    isDark ? brandColor(palette, dark, true) : brandColor(palette, light, false);
  return {
    card: role('secondary', '50', '100'),
    outline: role('secondary', '200', '300'),
    shadow: role('secondary', '200', '300'),
    tile: role('secondary', '100', '200'),
    glyph: role('secondary', '400', '500'),
    liftOutline: role('primary', '200', '100'),
    liftTile: role('primary', '200', '100'),
    liftGlyph: role('primary', '500', '500'),
  };
}

export type FlashcardsEmptyArtworkPalette = ReturnType<
  typeof flashcardsEmptyArtworkPalette
>;

export interface FlashcardsEmptyArtworkProps {
  /** Rendered width in DS points. Keep the 5:4 aspect of the default box. */
  width?: number;
  /** Rendered height in DS points. */
  height?: number;
}
